import Spinner from '@rio-cloud/rio-uikit/Spinner';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../../api/redux/api.redux';
import { ApiCalls } from '../../api/redux/types';
import { fetchPermissionsThunk } from './permissions.thunk';
import { getPermissions } from './redux/permissions.redux';

interface WrapperProps {
    fetchPermissions: () => void;
    permissions?: string[];
    hasError: boolean;
    isLoading: boolean;
}

export interface PermissionValidatorWrappedComponentProps {
    userHasPermissions: (requiredPermissions: string[]) => boolean;
}

export function createPermissionWrapper<T>(ErrorStateComponent: React.ComponentType) {
    return (WrappedComponent: React.ComponentType<T & PermissionValidatorWrappedComponentProps>) => {
        return class WithPermissions extends React.Component<WrapperProps & T> {
            constructor(props: WrapperProps & T) {
                super(props);
                this.props.fetchPermissions();

                this.hasSingleRequiredPermission = this.hasSingleRequiredPermission.bind(this);
                this.userHasPermissions = this.userHasPermissions.bind(this);
            }

            userHasPermissions(requiredPermissions: string[]) {
                if (!Array.isArray(requiredPermissions)) {
                    throw new Error('userHasPermissions expects an array as argument.');
                }
                return requiredPermissions.every(this.hasSingleRequiredPermission);
            }

            hasSingleRequiredPermission(requiredPermission: string) {
                return Array.isArray(this.props.permissions) && this.props.permissions.includes(requiredPermission);
            }

            renderLoadingSpinner() {
                return (
                    <div>
                        <Spinner text={<FormattedMessage id={'intl-msg:marketplace.loading'} />} isInverse={false} />
                    </div>
                );
            }

            render() {
                if (this.props.hasError) {
                    return <ErrorStateComponent />;
                }

                if (this.props.isLoading || !this.props.permissions) {
                    return this.renderLoadingSpinner();
                }

                // Used to pass only original props to component, hide permission props
                const { permissions, fetchPermissions, isLoading, hasError, ...otherProps } = this.props;

                return <WrappedComponent {...(otherProps as T)} userHasPermissions={this.userHasPermissions} />;
            }
        };
    };
}

export function mapStateToProps(state: RootState) {
    return {
        permissions: getPermissions(state),
        hasError: getHasError(state, ApiCalls.PERMISSIONS),
        isLoading: getIsLoading(state, ApiCalls.PERMISSIONS),
    };
}

export function mapDispatchToProps(dispatch: RootDispatch) {
    return {
        fetchPermissions: () => dispatch(fetchPermissionsThunk),
    };
}

export function wrapWithPermissionsValidator<T>(ErrorStateComponent: React.ComponentType) {
    return (WrappedComponent: React.ComponentType<T & PermissionValidatorWrappedComponentProps>) => {
        const PermissionWrapper = createPermissionWrapper<T>(ErrorStateComponent)(WrappedComponent);
        const ConnectedPermissionWrapper = connect(
            mapStateToProps,
            mapDispatchToProps
        )(PermissionWrapper as unknown as React.ComponentType<WrapperProps>);
        return ConnectedPermissionWrapper as unknown as React.ComponentType<T>;
    };
}
